import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  Copy,
  Settings,
  Type,
  Image,
  Columns,
  Grid3X3,
  MousePointer,
  Quote,
  Video,
  Images,
  Mail,
  DollarSign,
  Minus,
  Layout,
  Loader2,
  X,
  Monitor,
  Tablet,
  Smartphone,
  Palette,
  Move,
  RotateCcw,
  RotateCw,
  Layers,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown as ChevronDownIcon,
  HelpCircle,
  Users,
  Star,
  Clock,
  Award,
  Sparkles,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUpload from "../../components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL;

// Section type definitions
const SECTION_TYPES = [
  { type: "hero", label: "Hero Banner", icon: Layout, category: "Headers", description: "Full-width hero with background" },
  { type: "text", label: "Text Block", icon: Type, category: "Content", description: "Heading and rich text" },
  { type: "image", label: "Image", icon: Image, category: "Media", description: "Single image with caption" },
  { type: "divider", label: "Divider", icon: Minus, category: "Layout", description: "Visual separator" },
  { type: "spacer", label: "Spacer", icon: Layers, category: "Layout", description: "Empty space" },
  { type: "two_column", label: "Two Columns", icon: Columns, category: "Layout", description: "Side-by-side layout" },
  { type: "three_column", label: "Three Columns", icon: Grid3X3, category: "Layout", description: "Three column grid" },
  { type: "cta", label: "Call to Action", icon: MousePointer, category: "Conversion", description: "Button with text" },
  { type: "testimonial", label: "Testimonial", icon: Quote, category: "Social Proof", description: "Customer quote" },
  { type: "testimonials_grid", label: "Testimonials Grid", icon: Star, category: "Social Proof", description: "Multiple testimonials" },
  { type: "faq", label: "FAQ Accordion", icon: HelpCircle, category: "Content", description: "Collapsible questions" },
  { type: "team", label: "Team Section", icon: Users, category: "Content", description: "Team member cards" },
  { type: "features", label: "Features Grid", icon: Award, category: "Content", description: "Icon feature boxes" },
  { type: "stats", label: "Stats Counter", icon: Sparkles, category: "Social Proof", description: "Animated numbers" },
  { type: "countdown", label: "Countdown Timer", icon: Clock, category: "Conversion", description: "Event countdown" },
  { type: "video", label: "Video Embed", icon: Video, category: "Media", description: "YouTube or Vimeo" },
  { type: "gallery", label: "Image Gallery", icon: Images, category: "Media", description: "Image grid" },
  { type: "contact_form", label: "Contact Form", icon: Mail, category: "Conversion", description: "Contact submission" },
  { type: "pricing", label: "Pricing Table", icon: DollarSign, category: "Conversion", description: "Pricing cards" },
];

// Default section styles
const DEFAULT_SECTION_STYLES = {
  paddingTop: 60,
  paddingBottom: 60,
  paddingLeft: 20,
  paddingRight: 20,
  marginTop: 0,
  marginBottom: 0,
  backgroundColor: "transparent",
  backgroundImage: "",
  backgroundOverlay: 0,
  backgroundPosition: "center",
  backgroundSize: "cover",
  borderRadius: 0,
  borderWidth: 0,
  borderColor: "rgba(212,175,55,0.3)",
  boxShadow: "none",
  maxWidth: "1280",
  fullWidth: false,
};

// Responsive breakpoints
const BREAKPOINTS = {
  desktop: { icon: Monitor, label: "Desktop", width: "100%" },
  tablet: { icon: Tablet, label: "Tablet", width: "768px" },
  mobile: { icon: Smartphone, label: "Mobile", width: "375px" },
};

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${API}${url}`;
  return url;
};

// Style Panel Component
const StylePanel = ({ styles, onChange, onClose }) => {
  const [activeTab, setActiveTab] = useState("spacing");
  
  const updateStyle = (key, value) => {
    onChange({ ...styles, [key]: value });
  };

  return (
    <div className="bg-[#0A0A0A] border-l border-[rgba(212,175,55,0.2)] w-80 h-full overflow-y-auto">
      <div className="sticky top-0 bg-[#0A0A0A] border-b border-[rgba(212,175,55,0.2)] p-3 flex items-center justify-between z-10">
        <h3 className="text-[#D4AF37] font-semibold flex items-center gap-2">
          <Palette className="w-4 h-4" /> Section Styles
        </h3>
        <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[rgba(212,175,55,0.2)]">
        {["spacing", "background", "border", "advanced"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 text-xs font-medium capitalize transition-colors ${
              activeTab === tab
                ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                : "text-[#A1A1AA] hover:text-[#F5F5F0]"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-4">
        {/* Spacing Tab */}
        {activeTab === "spacing" && (
          <>
            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Padding (px)</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Top</span>
                  <input
                    type="number"
                    value={styles.paddingTop || 0}
                    onChange={(e) => updateStyle("paddingTop", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Bottom</span>
                  <input
                    type="number"
                    value={styles.paddingBottom || 0}
                    onChange={(e) => updateStyle("paddingBottom", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Left</span>
                  <input
                    type="number"
                    value={styles.paddingLeft || 0}
                    onChange={(e) => updateStyle("paddingLeft", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Right</span>
                  <input
                    type="number"
                    value={styles.paddingRight || 0}
                    onChange={(e) => updateStyle("paddingRight", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Margin (px)</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Top</span>
                  <input
                    type="number"
                    value={styles.marginTop || 0}
                    onChange={(e) => updateStyle("marginTop", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <span className="text-[10px] text-[#a1a1aa]">Bottom</span>
                  <input
                    type="number"
                    value={styles.marginBottom || 0}
                    onChange={(e) => updateStyle("marginBottom", parseInt(e.target.value) || 0)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Content Width</label>
              <select
                value={styles.maxWidth || "1280"}
                onChange={(e) => updateStyle("maxWidth", e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="640">Narrow (640px)</option>
                <option value="768">Small (768px)</option>
                <option value="1024">Medium (1024px)</option>
                <option value="1280">Large (1280px)</option>
                <option value="1536">XL (1536px)</option>
                <option value="full">Full Width</option>
              </select>
            </div>
          </>
        )}

        {/* Background Tab */}
        {activeTab === "background" && (
          <>
            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Background Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={styles.backgroundColor === "transparent" ? "#050505" : styles.backgroundColor}
                  onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                  className="w-10 h-10 bg-transparent border border-[rgba(212,175,55,0.2)] cursor-pointer"
                />
                <input
                  type="text"
                  value={styles.backgroundColor || "transparent"}
                  onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                  className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  placeholder="transparent, #000, rgba(...)"
                />
              </div>
              <div className="flex gap-1 mt-2">
                {["transparent", "#050505", "#0A0A0A", "#0F0F0F", "#1A1A1A", "#D4AF37"].map((color) => (
                  <button
                    key={color}
                    onClick={() => updateStyle("backgroundColor", color)}
                    className={`w-6 h-6 rounded border ${
                      styles.backgroundColor === color ? "border-[#D4AF37]" : "border-[rgba(212,175,55,0.2)]"
                    }`}
                    style={{ backgroundColor: color === "transparent" ? "transparent" : color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Background Image</label>
              <ImageUpload
                value={styles.backgroundImage || ""}
                onChange={(url) => updateStyle("backgroundImage", url)}
                folder="backgrounds"
                previewHeight="h-20"
                label=""
              />
            </div>

            {styles.backgroundImage && (
              <>
                <div>
                  <label className="text-xs text-[#A1A1AA] mb-2 block">
                    Overlay Darkness ({styles.backgroundOverlay || 0}%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="90"
                    value={styles.backgroundOverlay || 0}
                    onChange={(e) => updateStyle("backgroundOverlay", parseInt(e.target.value))}
                    className="w-full accent-[#D4AF37]"
                  />
                </div>

                <div>
                  <label className="text-xs text-[#A1A1AA] mb-2 block">Position</label>
                  <select
                    value={styles.backgroundPosition || "center"}
                    onChange={(e) => updateStyle("backgroundPosition", e.target.value)}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  >
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </>
            )}
          </>
        )}

        {/* Border Tab */}
        {activeTab === "border" && (
          <>
            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Border Radius (px)</label>
              <input
                type="range"
                min="0"
                max="50"
                value={styles.borderRadius || 0}
                onChange={(e) => updateStyle("borderRadius", parseInt(e.target.value))}
                className="w-full accent-[#D4AF37]"
              />
              <span className="text-xs text-[#a1a1aa]">{styles.borderRadius || 0}px</span>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Border Width (px)</label>
              <input
                type="range"
                min="0"
                max="10"
                value={styles.borderWidth || 0}
                onChange={(e) => updateStyle("borderWidth", parseInt(e.target.value))}
                className="w-full accent-[#D4AF37]"
              />
              <span className="text-xs text-[#a1a1aa]">{styles.borderWidth || 0}px</span>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Border Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={styles.borderColor?.startsWith("rgba") ? "#D4AF37" : (styles.borderColor || "#D4AF37")}
                  onChange={(e) => updateStyle("borderColor", e.target.value)}
                  className="w-10 h-10 bg-transparent border border-[rgba(212,175,55,0.2)] cursor-pointer"
                />
                <input
                  type="text"
                  value={styles.borderColor || ""}
                  onChange={(e) => updateStyle("borderColor", e.target.value)}
                  className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Box Shadow</label>
              <select
                value={styles.boxShadow || "none"}
                onChange={(e) => updateStyle("boxShadow", e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="none">None</option>
                <option value="0 4px 6px -1px rgba(0,0,0,0.3)">Small</option>
                <option value="0 10px 15px -3px rgba(0,0,0,0.4)">Medium</option>
                <option value="0 20px 25px -5px rgba(0,0,0,0.5)">Large</option>
                <option value="0 0 30px rgba(212,175,55,0.2)">Gold Glow</option>
              </select>
            </div>
          </>
        )}

        {/* Advanced Tab */}
        {activeTab === "advanced" && (
          <>
            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Section ID (for anchors)</label>
              <input
                type="text"
                value={styles.sectionId || ""}
                onChange={(e) => updateStyle("sectionId", e.target.value.replace(/\s/g, "-").toLowerCase())}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="my-section"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Custom CSS Class</label>
              <input
                type="text"
                value={styles.customClass || ""}
                onChange={(e) => updateStyle("customClass", e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="my-custom-class"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">Animation</label>
              <select
                value={styles.animation || "none"}
                onChange={(e) => updateStyle("animation", e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="none">None</option>
                <option value="fadeIn">Fade In</option>
                <option value="slideUp">Slide Up</option>
                <option value="slideLeft">Slide Left</option>
                <option value="slideRight">Slide Right</option>
                <option value="zoomIn">Zoom In</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={styles.sticky || false}
                onChange={(e) => updateStyle("sticky", e.target.checked)}
                className="accent-[#D4AF37]"
              />
              <label className="text-xs text-[#A1A1AA]">Sticky Section</label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={styles.hideOnMobile || false}
                onChange={(e) => updateStyle("hideOnMobile", e.target.checked)}
                className="accent-[#D4AF37]"
              />
              <label className="text-xs text-[#A1A1AA]">Hide on Mobile</label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Section Content Editors
const HeroEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Title</label>
      <input
        type="text"
        value={content.title || ""}
        onChange={(e) => onChange({ ...content, title: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Hero Title"
      />
    </div>
    <div>
      <label className="form-label">Subtitle</label>
      <input
        type="text"
        value={content.subtitle || ""}
        onChange={(e) => onChange({ ...content, subtitle: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Hero Subtitle"
      />
    </div>
    <div>
      <label className="form-label">Text Alignment</label>
      <div className="flex gap-2">
        {["left", "center", "right"].map((align) => (
          <button
            key={align}
            onClick={() => onChange({ ...content, alignment: align })}
            className={`flex-1 py-2 border ${
              (content.alignment || "center") === align
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[rgba(212,175,55,0.2)] text-[#A1A1AA]"
            }`}
          >
            {align === "left" && <AlignLeft className="w-4 h-4 mx-auto" />}
            {align === "center" && <AlignCenter className="w-4 h-4 mx-auto" />}
            {align === "right" && <AlignRight className="w-4 h-4 mx-auto" />}
          </button>
        ))}
      </div>
    </div>
    <ImageUpload
      label="Background Image"
      value={content.background_image || ""}
      onChange={(url) => onChange({ ...content, background_image: url })}
      folder="hero"
      previewHeight="h-32"
    />
    <div>
      <label className="form-label">Overlay Opacity ({content.overlay_opacity || 60}%)</label>
      <input
        type="range"
        min="0"
        max="100"
        value={content.overlay_opacity || 60}
        onChange={(e) => onChange({ ...content, overlay_opacity: parseInt(e.target.value) })}
        className="w-full accent-[#D4AF37]"
      />
    </div>
    <div>
      <label className="form-label">Min Height</label>
      <select
        value={content.minHeight || "60vh"}
        onChange={(e) => onChange({ ...content, minHeight: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="40vh">Small (40vh)</option>
        <option value="60vh">Medium (60vh)</option>
        <option value="80vh">Large (80vh)</option>
        <option value="100vh">Full Screen</option>
      </select>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="form-label">CTA Button Text</label>
        <input
          type="text"
          value={content.cta_text || ""}
          onChange={(e) => onChange({ ...content, cta_text: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Learn More"
        />
      </div>
      <div>
        <label className="form-label">CTA Link</label>
        <input
          type="text"
          value={content.cta_link || ""}
          onChange={(e) => onChange({ ...content, cta_link: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="/contact"
        />
      </div>
    </div>
    <div>
      <label className="form-label">Button Style</label>
      <select
        value={content.cta_style || "solid"}
        onChange={(e) => onChange({ ...content, cta_style: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="solid">Solid Gold</option>
        <option value="outline">Outline</option>
        <option value="ghost">Ghost</option>
      </select>
    </div>
  </div>
);

const TextEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Heading</label>
      <input
        type="text"
        value={content.heading || ""}
        onChange={(e) => onChange({ ...content, heading: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
      />
    </div>
    <div>
      <label className="form-label">Heading Size</label>
      <select
        value={content.headingSize || "3xl"}
        onChange={(e) => onChange({ ...content, headingSize: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="xl">Small (XL)</option>
        <option value="2xl">Medium (2XL)</option>
        <option value="3xl">Large (3XL)</option>
        <option value="4xl">XL (4XL)</option>
        <option value="5xl">XXL (5XL)</option>
      </select>
    </div>
    <div>
      <label className="form-label">Content</label>
      <RichTextEditor
        value={content.text || ""}
        onChange={(val) => onChange({ ...content, text: val })}
        placeholder="Enter your content here..."
      />
    </div>
    <div>
      <label className="form-label">Text Alignment</label>
      <div className="flex gap-2">
        {["left", "center", "right"].map((align) => (
          <button
            key={align}
            onClick={() => onChange({ ...content, alignment: align })}
            className={`flex-1 py-2 border ${
              (content.alignment || "left") === align
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[rgba(212,175,55,0.2)] text-[#A1A1AA]"
            }`}
          >
            {align === "left" && <AlignLeft className="w-4 h-4 mx-auto" />}
            {align === "center" && <AlignCenter className="w-4 h-4 mx-auto" />}
            {align === "right" && <AlignRight className="w-4 h-4 mx-auto" />}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const SpacerEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Height ({content.height || 60}px)</label>
      <input
        type="range"
        min="20"
        max="200"
        value={content.height || 60}
        onChange={(e) => onChange({ ...content, height: parseInt(e.target.value) })}
        className="w-full accent-[#D4AF37]"
      />
    </div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={content.showLine || false}
        onChange={(e) => onChange({ ...content, showLine: e.target.checked })}
        className="accent-[#D4AF37]"
      />
      <label className="text-[#A1A1AA] text-sm">Show divider line</label>
    </div>
  </div>
);

const FAQEditor = ({ content, onChange }) => {
  const items = content.items || [{ question: "", answer: "" }];
  
  const addItem = () => {
    onChange({ ...content, items: [...items, { question: "", answer: "" }] });
  };
  
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, items: updated });
  };
  
  const removeItem = (index) => {
    onChange({ ...content, items: items.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Frequently Asked Questions"
        />
      </div>
      
      <div className="space-y-3">
        <label className="form-label">Questions ({items.length})</label>
        {items.map((item, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Question {index + 1}</span>
              <button onClick={() => removeItem(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <input
              type="text"
              value={item.question || ""}
              onChange={(e) => updateItem(index, "question", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Question"
            />
            <textarea
              value={item.answer || ""}
              onChange={(e) => updateItem(index, "answer", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm h-20 resize-none focus:border-[#D4AF37] focus:outline-none"
              placeholder="Answer"
            />
          </div>
        ))}
        <button
          onClick={addItem}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Question
        </button>
      </div>
    </div>
  );
};

const TeamEditor = ({ content, onChange }) => {
  const members = content.members || [];
  
  const addMember = () => {
    onChange({ 
      ...content, 
      members: [...members, { name: "", role: "", image: "", bio: "" }] 
    });
  };
  
  const updateMember = (index, field, value) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, members: updated });
  };
  
  const removeMember = (index) => {
    onChange({ ...content, members: members.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Meet Our Team"
        />
      </div>
      <div>
        <label className="form-label">Columns</label>
        <select
          value={content.columns || 3}
          onChange={(e) => onChange({ ...content, columns: parseInt(e.target.value) })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
        >
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
          <option value={4}>4 Columns</option>
        </select>
      </div>
      
      <div className="space-y-3">
        {members.map((member, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Member {index + 1}</span>
              <button onClick={() => removeMember(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <ImageUpload
              value={member.image || ""}
              onChange={(url) => updateMember(index, "image", url)}
              folder="team"
              previewHeight="h-16"
              label=""
            />
            <input
              type="text"
              value={member.name || ""}
              onChange={(e) => updateMember(index, "name", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Name"
            />
            <input
              type="text"
              value={member.role || ""}
              onChange={(e) => updateMember(index, "role", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Role"
            />
          </div>
        ))}
        <button
          onClick={addMember}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Team Member
        </button>
      </div>
    </div>
  );
};

const FeaturesEditor = ({ content, onChange }) => {
  const features = content.features || [];
  
  const addFeature = () => {
    onChange({ 
      ...content, 
      features: [...features, { icon: "star", title: "", description: "" }] 
    });
  };
  
  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, features: updated });
  };
  
  const removeFeature = (index) => {
    onChange({ ...content, features: features.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Our Features"
        />
      </div>
      <div>
        <label className="form-label">Columns</label>
        <select
          value={content.columns || 3}
          onChange={(e) => onChange({ ...content, columns: parseInt(e.target.value) })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
        >
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
          <option value={4}>4 Columns</option>
        </select>
      </div>
      
      <div className="space-y-3">
        {features.map((feature, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Feature {index + 1}</span>
              <button onClick={() => removeFeature(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <select
              value={feature.icon || "star"}
              onChange={(e) => updateFeature(index, "icon", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="star">Star</option>
              <option value="check">Checkmark</option>
              <option value="heart">Heart</option>
              <option value="award">Award</option>
              <option value="zap">Lightning</option>
              <option value="shield">Shield</option>
              <option value="clock">Clock</option>
              <option value="users">Users</option>
            </select>
            <input
              type="text"
              value={feature.title || ""}
              onChange={(e) => updateFeature(index, "title", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Feature Title"
            />
            <textarea
              value={feature.description || ""}
              onChange={(e) => updateFeature(index, "description", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm h-16 resize-none focus:border-[#D4AF37] focus:outline-none"
              placeholder="Description"
            />
          </div>
        ))}
        <button
          onClick={addFeature}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Feature
        </button>
      </div>
    </div>
  );
};

const StatsEditor = ({ content, onChange }) => {
  const stats = content.stats || [];
  
  const addStat = () => {
    onChange({ 
      ...content, 
      stats: [...stats, { value: "100", suffix: "+", label: "" }] 
    });
  };
  
  const updateStat = (index, field, value) => {
    const updated = [...stats];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, stats: updated });
  };
  
  const removeStat = (index) => {
    onChange({ ...content, stats: stats.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title (optional)</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Our Impact"
        />
      </div>
      
      <div className="space-y-3">
        {stats.map((stat, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Stat {index + 1}</span>
              <button onClick={() => removeStat(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={stat.value || ""}
                onChange={(e) => updateStat(index, "value", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="100"
              />
              <input
                type="text"
                value={stat.suffix || ""}
                onChange={(e) => updateStat(index, "suffix", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="+ or %"
              />
            </div>
            <input
              type="text"
              value={stat.label || ""}
              onChange={(e) => updateStat(index, "label", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Happy Customers"
            />
          </div>
        ))}
        <button
          onClick={addStat}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Stat
        </button>
      </div>
    </div>
  );
};

const CountdownEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Event Title</label>
      <input
        type="text"
        value={content.title || ""}
        onChange={(e) => onChange({ ...content, title: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Event Starts In"
      />
    </div>
    <div>
      <label className="form-label">Target Date & Time</label>
      <input
        type="datetime-local"
        value={content.targetDate || ""}
        onChange={(e) => onChange({ ...content, targetDate: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
      />
    </div>
    <div>
      <label className="form-label">Expired Message</label>
      <input
        type="text"
        value={content.expiredMessage || ""}
        onChange={(e) => onChange({ ...content, expiredMessage: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Event has started!"
      />
    </div>
  </div>
);

// Image Section Editor
const ImageEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <ImageUpload
      label="Image"
      value={content.image || ""}
      onChange={(url) => onChange({ ...content, image: url })}
      folder="content"
      previewHeight="h-40"
    />
    <div>
      <label className="form-label">Caption (optional)</label>
      <input
        type="text"
        value={content.caption || ""}
        onChange={(e) => onChange({ ...content, caption: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Image description"
      />
    </div>
    <div>
      <label className="form-label">Alt Text (SEO)</label>
      <input
        type="text"
        value={content.alt || ""}
        onChange={(e) => onChange({ ...content, alt: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Describe the image"
      />
    </div>
    <div>
      <label className="form-label">Image Size</label>
      <select
        value={content.size || "full"}
        onChange={(e) => onChange({ ...content, size: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="small">Small (50%)</option>
        <option value="medium">Medium (75%)</option>
        <option value="full">Full Width</option>
      </select>
    </div>
    <div>
      <label className="form-label">Alignment</label>
      <div className="flex gap-2">
        {["left", "center", "right"].map((align) => (
          <button
            key={align}
            onClick={() => onChange({ ...content, alignment: align })}
            className={`flex-1 py-2 border capitalize ${
              (content.alignment || "center") === align
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[rgba(212,175,55,0.2)] text-[#A1A1AA]"
            }`}
          >
            {align}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="form-label">Link URL (optional)</label>
      <input
        type="text"
        value={content.link || ""}
        onChange={(e) => onChange({ ...content, link: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="/page or https://..."
      />
    </div>
  </div>
);

// Divider Editor
const DividerEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Style</label>
      <select
        value={content.style || "solid"}
        onChange={(e) => onChange({ ...content, style: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="solid">Solid Line</option>
        <option value="dashed">Dashed Line</option>
        <option value="dotted">Dotted Line</option>
        <option value="gradient">Gold Gradient</option>
        <option value="ornament">Ornament</option>
      </select>
    </div>
    <div>
      <label className="form-label">Width</label>
      <select
        value={content.width || "50%"}
        onChange={(e) => onChange({ ...content, width: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="25%">Short (25%)</option>
        <option value="50%">Medium (50%)</option>
        <option value="75%">Long (75%)</option>
        <option value="100%">Full Width</option>
      </select>
    </div>
    <div>
      <label className="form-label">Thickness ({content.thickness || 1}px)</label>
      <input
        type="range"
        min="1"
        max="5"
        value={content.thickness || 1}
        onChange={(e) => onChange({ ...content, thickness: parseInt(e.target.value) })}
        className="w-full accent-[#D4AF37]"
      />
    </div>
    <div>
      <label className="form-label">Color</label>
      <div className="flex gap-2">
        <input
          type="color"
          value={content.color || "#D4AF37"}
          onChange={(e) => onChange({ ...content, color: e.target.value })}
          className="w-10 h-10 bg-transparent border border-[rgba(212,175,55,0.2)] cursor-pointer"
        />
        <input
          type="text"
          value={content.color || "#D4AF37"}
          onChange={(e) => onChange({ ...content, color: e.target.value })}
          className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
        />
      </div>
    </div>
  </div>
);

// Two Column Layout Editor
const TwoColumnEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Column Split</label>
      <select
        value={content.split || "50-50"}
        onChange={(e) => onChange({ ...content, split: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="50-50">Equal (50/50)</option>
        <option value="33-67">One Third / Two Thirds</option>
        <option value="67-33">Two Thirds / One Third</option>
        <option value="40-60">40/60</option>
        <option value="60-40">60/40</option>
      </select>
    </div>
    <div>
      <label className="form-label">Gap Between Columns</label>
      <select
        value={content.gap || "8"}
        onChange={(e) => onChange({ ...content, gap: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="4">Small (1rem)</option>
        <option value="8">Medium (2rem)</option>
        <option value="12">Large (3rem)</option>
        <option value="16">XL (4rem)</option>
      </select>
    </div>
    <div className="p-3 bg-[#121212] rounded border border-[rgba(212,175,55,0.2)]">
      <label className="form-label">Left Column</label>
      <RichTextEditor
        value={content.leftContent || ""}
        onChange={(val) => onChange({ ...content, leftContent: val })}
        placeholder="Left column content..."
      />
    </div>
    <div className="p-3 bg-[#121212] rounded border border-[rgba(212,175,55,0.2)]">
      <label className="form-label">Right Column</label>
      <RichTextEditor
        value={content.rightContent || ""}
        onChange={(val) => onChange({ ...content, rightContent: val })}
        placeholder="Right column content..."
      />
    </div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={content.reverseOnMobile || false}
        onChange={(e) => onChange({ ...content, reverseOnMobile: e.target.checked })}
        className="accent-[#D4AF37]"
      />
      <label className="text-[#A1A1AA] text-sm">Reverse order on mobile</label>
    </div>
  </div>
);

// Three Column Layout Editor
const ThreeColumnEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Gap Between Columns</label>
      <select
        value={content.gap || "6"}
        onChange={(e) => onChange({ ...content, gap: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="4">Small (1rem)</option>
        <option value="6">Medium (1.5rem)</option>
        <option value="8">Large (2rem)</option>
      </select>
    </div>
    {[0, 1, 2].map((idx) => (
      <div key={idx} className="p-3 bg-[#121212] rounded border border-[rgba(212,175,55,0.2)]">
        <label className="form-label">Column {idx + 1}</label>
        <RichTextEditor
          value={(content.columns || [])[idx] || ""}
          onChange={(val) => {
            const cols = [...(content.columns || ["", "", ""])];
            cols[idx] = val;
            onChange({ ...content, columns: cols });
          }}
          placeholder={`Column ${idx + 1} content...`}
        />
      </div>
    ))}
  </div>
);

// CTA Button Editor
const CTAEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Heading</label>
      <input
        type="text"
        value={content.heading || ""}
        onChange={(e) => onChange({ ...content, heading: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Ready to get started?"
      />
    </div>
    <div>
      <label className="form-label">Subtext</label>
      <input
        type="text"
        value={content.subtext || ""}
        onChange={(e) => onChange({ ...content, subtext: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Join thousands of happy customers"
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="form-label">Button Text</label>
        <input
          type="text"
          value={content.buttonText || ""}
          onChange={(e) => onChange({ ...content, buttonText: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Get Started"
        />
      </div>
      <div>
        <label className="form-label">Button Link</label>
        <input
          type="text"
          value={content.buttonLink || ""}
          onChange={(e) => onChange({ ...content, buttonLink: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="/contact"
        />
      </div>
    </div>
    <div>
      <label className="form-label">Button Style</label>
      <select
        value={content.buttonStyle || "solid"}
        onChange={(e) => onChange({ ...content, buttonStyle: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="solid">Solid Gold</option>
        <option value="outline">Outline</option>
        <option value="ghost">Ghost (Text Only)</option>
      </select>
    </div>
    <div>
      <label className="form-label">Alignment</label>
      <div className="flex gap-2">
        {["left", "center", "right"].map((align) => (
          <button
            key={align}
            onClick={() => onChange({ ...content, alignment: align })}
            className={`flex-1 py-2 border capitalize ${
              (content.alignment || "center") === align
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[rgba(212,175,55,0.2)] text-[#A1A1AA]"
            }`}
          >
            {align}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// Single Testimonial Editor
const TestimonialEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Quote</label>
      <textarea
        value={content.quote || ""}
        onChange={(e) => onChange({ ...content, quote: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 h-24 resize-none focus:border-[#D4AF37] focus:outline-none"
        placeholder="What the customer said..."
      />
    </div>
    <ImageUpload
      label="Author Photo"
      value={content.authorImage || ""}
      onChange={(url) => onChange({ ...content, authorImage: url })}
      folder="testimonials"
      previewHeight="h-20"
    />
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="form-label">Name</label>
        <input
          type="text"
          value={content.authorName || ""}
          onChange={(e) => onChange({ ...content, authorName: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Jane Smith"
        />
      </div>
      <div>
        <label className="form-label">Title/Role</label>
        <input
          type="text"
          value={content.authorTitle || ""}
          onChange={(e) => onChange({ ...content, authorTitle: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="CEO, Company"
        />
      </div>
    </div>
    <div>
      <label className="form-label">Star Rating</label>
      <select
        value={content.rating || 5}
        onChange={(e) => onChange({ ...content, rating: parseInt(e.target.value) })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value={5}>5 Stars</option>
        <option value={4}>4 Stars</option>
        <option value={3}>3 Stars</option>
        <option value={0}>No Rating</option>
      </select>
    </div>
  </div>
);

// Testimonials Grid Editor
const TestimonialsGridEditor = ({ content, onChange }) => {
  const testimonials = content.testimonials || [];
  
  const addTestimonial = () => {
    onChange({ 
      ...content, 
      testimonials: [...testimonials, { quote: "", authorName: "", authorTitle: "", rating: 5 }] 
    });
  };
  
  const updateTestimonial = (index, field, value) => {
    const updated = [...testimonials];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, testimonials: updated });
  };
  
  const removeTestimonial = (index) => {
    onChange({ ...content, testimonials: testimonials.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="What Our Clients Say"
        />
      </div>
      <div>
        <label className="form-label">Columns</label>
        <select
          value={content.columns || 3}
          onChange={(e) => onChange({ ...content, columns: parseInt(e.target.value) })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
        >
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
        </select>
      </div>
      
      <div className="space-y-3">
        {testimonials.map((t, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Testimonial {index + 1}</span>
              <button onClick={() => removeTestimonial(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <textarea
              value={t.quote || ""}
              onChange={(e) => updateTestimonial(index, "quote", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm h-16 resize-none focus:border-[#D4AF37] focus:outline-none"
              placeholder="Quote"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={t.authorName || ""}
                onChange={(e) => updateTestimonial(index, "authorName", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="Name"
              />
              <input
                type="text"
                value={t.authorTitle || ""}
                onChange={(e) => updateTestimonial(index, "authorTitle", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="Title"
              />
            </div>
          </div>
        ))}
        <button
          onClick={addTestimonial}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Testimonial
        </button>
      </div>
    </div>
  );
};

// Video Embed Editor
const VideoEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Video URL</label>
      <input
        type="text"
        value={content.url || ""}
        onChange={(e) => onChange({ ...content, url: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
      />
      <p className="text-[#a1a1aa] text-xs mt-1">Supports YouTube and Vimeo</p>
    </div>
    <div>
      <label className="form-label">Title (optional)</label>
      <input
        type="text"
        value={content.title || ""}
        onChange={(e) => onChange({ ...content, title: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Video title"
      />
    </div>
    <div>
      <label className="form-label">Aspect Ratio</label>
      <select
        value={content.aspectRatio || "16:9"}
        onChange={(e) => onChange({ ...content, aspectRatio: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="16:9">Widescreen (16:9)</option>
        <option value="4:3">Standard (4:3)</option>
        <option value="1:1">Square (1:1)</option>
        <option value="9:16">Vertical (9:16)</option>
      </select>
    </div>
    <div>
      <label className="form-label">Max Width</label>
      <select
        value={content.maxWidth || "full"}
        onChange={(e) => onChange({ ...content, maxWidth: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
      >
        <option value="640">Small (640px)</option>
        <option value="768">Medium (768px)</option>
        <option value="1024">Large (1024px)</option>
        <option value="full">Full Width</option>
      </select>
    </div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={content.autoplay || false}
        onChange={(e) => onChange({ ...content, autoplay: e.target.checked })}
        className="accent-[#D4AF37]"
      />
      <label className="text-[#A1A1AA] text-sm">Autoplay (muted)</label>
    </div>
  </div>
);

// Gallery Editor
const GalleryEditor = ({ content, onChange }) => {
  const images = content.images || [];
  
  const addImage = () => {
    onChange({ ...content, images: [...images, { url: "", caption: "" }] });
  };
  
  const updateImage = (index, field, value) => {
    const updated = [...images];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, images: updated });
  };
  
  const removeImage = (index) => {
    onChange({ ...content, images: images.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Gallery Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Our Gallery"
        />
      </div>
      <div>
        <label className="form-label">Columns</label>
        <select
          value={content.columns || 3}
          onChange={(e) => onChange({ ...content, columns: parseInt(e.target.value) })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
        >
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
          <option value={4}>4 Columns</option>
        </select>
      </div>
      <div>
        <label className="form-label">Style</label>
        <select
          value={content.style || "grid"}
          onChange={(e) => onChange({ ...content, style: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
        >
          <option value="grid">Grid</option>
          <option value="masonry">Masonry</option>
        </select>
      </div>
      
      <div className="space-y-3">
        <label className="form-label">Images ({images.length})</label>
        {images.map((img, index) => (
          <div key={index} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#D4AF37]">Image {index + 1}</span>
              <button onClick={() => removeImage(index)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
            <ImageUpload
              value={img.url || ""}
              onChange={(url) => updateImage(index, "url", url)}
              folder="gallery"
              previewHeight="h-16"
              label=""
            />
            <input
              type="text"
              value={img.caption || ""}
              onChange={(e) => updateImage(index, "caption", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              placeholder="Caption (optional)"
            />
          </div>
        ))}
        <button
          onClick={addImage}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Image
        </button>
      </div>
    </div>
  );
};

// Contact Form Editor
const ContactFormEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="form-label">Form Title</label>
      <input
        type="text"
        value={content.title || ""}
        onChange={(e) => onChange({ ...content, title: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Get In Touch"
      />
    </div>
    <div>
      <label className="form-label">Description</label>
      <textarea
        value={content.description || ""}
        onChange={(e) => onChange({ ...content, description: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 h-20 resize-none focus:border-[#D4AF37] focus:outline-none"
        placeholder="We'd love to hear from you..."
      />
    </div>
    <div>
      <label className="form-label">Fields to Show</label>
      <div className="space-y-2">
        {["name", "email", "phone", "subject", "message"].map((field) => (
          <label key={field} className="flex items-center gap-2 text-[#A1A1AA] capitalize">
            <input
              type="checkbox"
              checked={(content.fields || ["name", "email", "message"]).includes(field)}
              onChange={(e) => {
                const fields = content.fields || ["name", "email", "message"];
                if (e.target.checked) {
                  onChange({ ...content, fields: [...fields, field] });
                } else {
                  onChange({ ...content, fields: fields.filter(f => f !== field) });
                }
              }}
              className="accent-[#D4AF37]"
            />
            {field}
          </label>
        ))}
      </div>
    </div>
    <div>
      <label className="form-label">Submit Button Text</label>
      <input
        type="text"
        value={content.submitText || ""}
        onChange={(e) => onChange({ ...content, submitText: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Send Message"
      />
    </div>
    <div>
      <label className="form-label">Success Message</label>
      <input
        type="text"
        value={content.successMessage || ""}
        onChange={(e) => onChange({ ...content, successMessage: e.target.value })}
        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
        placeholder="Thanks! We'll be in touch soon."
      />
    </div>
  </div>
);

// Pricing Table Editor
const PricingEditor = ({ content, onChange }) => {
  const plans = content.plans || [];
  
  const addPlan = () => {
    onChange({ 
      ...content, 
      plans: [...plans, { name: "", price: "", period: "month", features: [], highlighted: false }] 
    });
  };
  
  const updatePlan = (index, field, value) => {
    const updated = [...plans];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...content, plans: updated });
  };
  
  const removePlan = (index) => {
    onChange({ ...content, plans: plans.filter((_, i) => i !== index) });
  };
  
  const addFeature = (planIndex) => {
    const updated = [...plans];
    updated[planIndex].features = [...(updated[planIndex].features || []), ""];
    onChange({ ...content, plans: updated });
  };
  
  const updateFeature = (planIndex, featureIndex, value) => {
    const updated = [...plans];
    updated[planIndex].features[featureIndex] = value;
    onChange({ ...content, plans: updated });
  };
  
  const removeFeature = (planIndex, featureIndex) => {
    const updated = [...plans];
    updated[planIndex].features = updated[planIndex].features.filter((_, i) => i !== featureIndex);
    onChange({ ...content, plans: updated });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Section Title</label>
        <input
          type="text"
          value={content.title || ""}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
          placeholder="Choose Your Plan"
        />
      </div>
      
      <div className="space-y-4">
        {plans.map((plan, planIdx) => (
          <div key={planIdx} className="p-3 border border-[rgba(212,175,55,0.2)] rounded space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-[#D4AF37]">Plan {planIdx + 1}</span>
              <button onClick={() => removePlan(planIdx)} className="text-red-500 hover:bg-red-500/10 p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={plan.name || ""}
                onChange={(e) => updatePlan(planIdx, "name", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="Plan Name"
              />
              <input
                type="text"
                value={plan.price || ""}
                onChange={(e) => updatePlan(planIdx, "price", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="$99"
              />
            </div>
            
            <select
              value={plan.period || "month"}
              onChange={(e) => updatePlan(planIdx, "period", e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="month">Per Month</option>
              <option value="year">Per Year</option>
              <option value="event">Per Event</option>
              <option value="person">Per Person</option>
              <option value="">One Time</option>
            </select>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={plan.highlighted || false}
                onChange={(e) => updatePlan(planIdx, "highlighted", e.target.checked)}
                className="accent-[#D4AF37]"
              />
              <label className="text-[#A1A1AA] text-sm">Highlight as Popular</label>
            </div>
            
            <div>
              <label className="text-xs text-[#a1a1aa] mb-1 block">Features</label>
              {(plan.features || []).map((feature, featIdx) => (
                <div key={featIdx} className="flex gap-2 mb-1">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(planIdx, featIdx, e.target.value)}
                    className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.1)] text-[#F5F5F0] p-1.5 text-xs focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Feature"
                  />
                  <button onClick={() => removeFeature(planIdx, featIdx)} className="text-red-400 text-xs px-2">×</button>
                </div>
              ))}
              <button
                onClick={() => addFeature(planIdx)}
                className="text-xs text-[#D4AF37] hover:underline mt-1"
              >
                + Add Feature
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={plan.buttonText || ""}
                onChange={(e) => updatePlan(planIdx, "buttonText", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="Button Text"
              />
              <input
                type="text"
                value={plan.buttonLink || ""}
                onChange={(e) => updatePlan(planIdx, "buttonLink", e.target.value)}
                className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                placeholder="/contact"
              />
            </div>
          </div>
        ))}
        <button
          onClick={addPlan}
          className="w-full py-2 border border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Pricing Plan
        </button>
      </div>
    </div>
  );
};

// Editor component map
const SECTION_EDITORS = {
  hero: HeroEditor,
  text: TextEditor,
  image: ImageEditor,
  divider: DividerEditor,
  spacer: SpacerEditor,
  two_column: TwoColumnEditor,
  three_column: ThreeColumnEditor,
  cta: CTAEditor,
  testimonial: TestimonialEditor,
  testimonials_grid: TestimonialsGridEditor,
  faq: FAQEditor,
  team: TeamEditor,
  features: FeaturesEditor,
  stats: StatsEditor,
  countdown: CountdownEditor,
  video: VideoEditor,
  gallery: GalleryEditor,
  contact_form: ContactFormEditor,
  pricing: PricingEditor,
};

// Main Visual Page Builder Component
const VisualPageBuilder = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Fetch page data
  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/pages/${pageId}`);
        setPage(res.data);
        if (res.data.sections?.length > 0) {
          setSelectedSection(res.data.sections[0].id);
        }
      } catch (error) {
        toast.error("Failed to load page");
        navigate("/admin/pages");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [pageId, navigate]);

  // Save to history for undo/redo
  const saveToHistory = useCallback((newPage) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newPage));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Update page with history
  const updatePage = useCallback((newPage) => {
    setPage(newPage);
    saveToHistory(newPage);
  }, [saveToHistory]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setPage(JSON.parse(history[historyIndex - 1]));
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setPage(JSON.parse(history[historyIndex + 1]));
    }
  }, [history, historyIndex]);

  // Save page
  const savePage = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/pages/${pageId}`, page);
      toast.success("Page saved!");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Add section
  const addSection = (type) => {
    const newSection = {
      id: `section-${Date.now()}`,
      type,
      order: page.sections.length,
      content: {},
      styles: { ...DEFAULT_SECTION_STYLES }
    };
    const newPage = { ...page, sections: [...page.sections, newSection] };
    updatePage(newPage);
    setSelectedSection(newSection.id);
    setShowAddSection(false);
  };

  // Update section content
  const updateSectionContent = (sectionId, content) => {
    const newPage = {
      ...page,
      sections: page.sections.map(s => s.id === sectionId ? { ...s, content } : s)
    };
    updatePage(newPage);
  };

  // Update section styles
  const updateSectionStyles = (sectionId, styles) => {
    const newPage = {
      ...page,
      sections: page.sections.map(s => s.id === sectionId ? { ...s, styles } : s)
    };
    updatePage(newPage);
  };

  // Delete section
  const deleteSection = (sectionId) => {
    if (window.confirm("Delete this section?")) {
      const newPage = {
        ...page,
        sections: page.sections.filter(s => s.id !== sectionId).map((s, i) => ({ ...s, order: i }))
      };
      updatePage(newPage);
      setSelectedSection(null);
    }
  };

  // Move section
  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= page.sections.length) return;
    
    const newSections = [...page.sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((s, i) => s.order = i);
    updatePage({ ...page, sections: newSections });
  };

  // Duplicate section
  const duplicateSection = (section) => {
    const newSection = {
      ...JSON.parse(JSON.stringify(section)),
      id: `section-${Date.now()}`,
      order: page.sections.length
    };
    updatePage({ ...page, sections: [...page.sections, newSection] });
    toast.success("Section duplicated");
  };

  // Get selected section
  const getSelectedSection = () => page?.sections?.find(s => s.id === selectedSection);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
        </div>
      </AdminLayout>
    );
  }

  const currentSection = getSelectedSection();

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-80px)] flex flex-col bg-[#050505]">
        {/* Top Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(212,175,55,0.2)] bg-[#0A0A0A]">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/pages")} className="text-[#A1A1AA] hover:text-[#D4AF37]">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <input
                type="text"
                value={page?.title || ""}
                onChange={(e) => setPage({ ...page, title: e.target.value })}
                className="bg-transparent text-lg font-semibold text-[#F5F5F0] font-medium placeholder-[#71717a] focus:outline-none border-b border-transparent focus:border-[#D4AF37] w-48"
              />
              <span className="text-xs text-[#a1a1aa] ml-2">/{page?.slug}</span>
            </div>
          </div>

          {/* Device Preview Toggles */}
          <div className="flex items-center gap-1 bg-[#121212] rounded p-1">
            {Object.entries(BREAKPOINTS).map(([key, { icon: Icon, label }]) => (
              <button
                key={key}
                onClick={() => setPreviewMode(key)}
                className={`p-2 rounded transition-colors ${
                  previewMode === key
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "text-[#A1A1AA] hover:text-[#F5F5F0]"
                }`}
                title={label}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-30"
              title="Undo"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-30"
              title="Redo"
            >
              <RotateCw className="w-4 h-4" />
            </button>
            <div className="w-px h-6 bg-[rgba(212,175,55,0.2)]" />
            <button
              onClick={savePage}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Section List */}
          <div className="w-64 border-r border-[rgba(212,175,55,0.2)] bg-[#0A0A0A] overflow-y-auto">
            <div className="p-3 border-b border-[rgba(212,175,55,0.2)]">
              <h3 className="text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider">
                Sections ({page?.sections?.length || 0})
              </h3>
            </div>
            
            <div className="p-2 space-y-1">
              {page?.sections?.map((section, index) => {
                const typeInfo = SECTION_TYPES.find(t => t.type === section.type);
                const Icon = typeInfo?.icon || Layout;
                const isSelected = selectedSection === section.id;
                
                return (
                  <div
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`p-3 rounded cursor-pointer transition-all ${
                      isSelected
                        ? "bg-[#D4AF37]/20 border border-[#D4AF37]"
                        : "hover:bg-[rgba(212,175,55,0.05)] border border-transparent"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-3 h-3 text-[#a1a1aa]" />
                        <Icon className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-sm text-[#F5F5F0]">{typeInfo?.label || section.type}</span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSection(index, 'up'); }}
                          disabled={index === 0}
                          className="p-1 text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-30"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSection(index, 'down'); }}
                          disabled={index === page.sections.length - 1}
                          className="p-1 text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-30"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-2">
              <button
                onClick={() => setShowAddSection(true)}
                className="w-full py-3 border-2 border-dashed border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-2 rounded"
              >
                <Plus className="w-5 h-5" /> Add Section
              </button>
            </div>
          </div>

          {/* Center - Live Preview */}
          <div className="flex-1 bg-[#121212] overflow-y-auto">
            <div className="sticky top-0 bg-[#121212] border-b border-[rgba(212,175,55,0.2)] p-2 text-center z-10">
              <span className="text-xs text-[#a1a1aa]">
                Live Preview — {BREAKPOINTS[previewMode].label}
              </span>
            </div>
            <div 
              className="mx-auto bg-[#050505] min-h-full transition-all duration-300"
              style={{ 
                maxWidth: previewMode === "desktop" ? "100%" : BREAKPOINTS[previewMode].width 
              }}
            >
              {page?.sections?.length === 0 ? (
                <div className="flex items-center justify-center h-96 text-[#a1a1aa]">
                  <div className="text-center">
                    <Layout className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No sections yet</p>
                    <p className="text-sm">Click "Add Section" to start building</p>
                  </div>
                </div>
              ) : (
                page?.sections?.map((section) => (
                  <PreviewSection
                    key={section.id}
                    section={section}
                    isSelected={selectedSection === section.id}
                    onClick={() => setSelectedSection(section.id)}
                    previewMode={previewMode}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Editor/Styles */}
          {currentSection && (
            <div className="w-80 border-l border-[rgba(212,175,55,0.2)] bg-[#0A0A0A] overflow-y-auto">
              {/* Section Actions */}
              <div className="p-3 border-b border-[rgba(212,175,55,0.2)] flex items-center justify-between">
                <span className="text-sm font-semibold text-[#D4AF37]">
                  {SECTION_TYPES.find(t => t.type === currentSection.type)?.label}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowStylePanel(!showStylePanel)}
                    className={`p-2 rounded ${showStylePanel ? 'bg-[#D4AF37] text-[#050505]' : 'text-[#A1A1AA] hover:text-[#D4AF37]'}`}
                    title="Style Settings"
                  >
                    <Palette className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => duplicateSection(currentSection)}
                    className="p-2 text-[#A1A1AA] hover:text-[#D4AF37]"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteSection(currentSection.id)}
                    className="p-2 text-[#A1A1AA] hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tabs: Content vs Styles */}
              <div className="flex border-b border-[rgba(212,175,55,0.2)]">
                <button
                  onClick={() => setShowStylePanel(false)}
                  className={`flex-1 py-2 text-sm font-medium ${
                    !showStylePanel ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A1A1AA]'
                  }`}
                >
                  Content
                </button>
                <button
                  onClick={() => setShowStylePanel(true)}
                  className={`flex-1 py-2 text-sm font-medium ${
                    showStylePanel ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A1A1AA]'
                  }`}
                >
                  Styles
                </button>
              </div>

              {/* Content/Style Panel */}
              <div className="p-4">
                {showStylePanel ? (
                  <StylePanel
                    styles={currentSection.styles || DEFAULT_SECTION_STYLES}
                    onChange={(styles) => updateSectionStyles(currentSection.id, styles)}
                    onClose={() => setShowStylePanel(false)}
                  />
                ) : (
                  <>
                    {SECTION_EDITORS[currentSection.type] ? (
                      React.createElement(SECTION_EDITORS[currentSection.type], {
                        content: currentSection.content,
                        onChange: (content) => updateSectionContent(currentSection.id, content)
                      })
                    ) : (
                      <p className="text-[#a1a1aa] text-sm">No editor available for this section type.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Section Modal */}
      {showAddSection && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(212,175,55,0.2)] sticky top-0 bg-[#0F0F0F]">
              <h3 className="text-xl font-semibold text-[#F5F5F0]">Add Section</h3>
              <button onClick={() => setShowAddSection(false)} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Group sections by category */}
            {["Headers", "Content", "Layout", "Media", "Social Proof", "Conversion"].map((category) => {
              const categoryTypes = SECTION_TYPES.filter(t => t.category === category);
              if (categoryTypes.length === 0) return null;
              
              return (
                <div key={category} className="p-4 border-b border-[rgba(212,175,55,0.1)]">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">{category}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {categoryTypes.map(({ type, label, icon: Icon, description }) => (
                      <button
                        key={type}
                        onClick={() => addSection(type)}
                        className="p-4 border border-[rgba(212,175,55,0.2)] hover:border-[#D4AF37] text-left transition-colors group rounded"
                      >
                        <Icon className="w-6 h-6 text-[#D4AF37] mb-2 group-hover:scale-110 transition-transform" />
                        <h4 className="font-medium text-[#F5F5F0] text-sm">{label}</h4>
                        <p className="text-xs text-[#a1a1aa] mt-1">{description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Preview Section Component
const PreviewSection = ({ section, isSelected, onClick, previewMode }) => {
  const styles = section.styles || DEFAULT_SECTION_STYLES;
  
  const sectionStyle = {
    paddingTop: `${styles.paddingTop}px`,
    paddingBottom: `${styles.paddingBottom}px`,
    paddingLeft: `${styles.paddingLeft}px`,
    paddingRight: `${styles.paddingRight}px`,
    marginTop: `${styles.marginTop}px`,
    marginBottom: `${styles.marginBottom}px`,
    backgroundColor: styles.backgroundColor,
    backgroundImage: styles.backgroundImage ? `url(${getImageUrl(styles.backgroundImage)})` : 'none',
    backgroundPosition: styles.backgroundPosition,
    backgroundSize: styles.backgroundSize,
    borderRadius: `${styles.borderRadius}px`,
    borderWidth: `${styles.borderWidth}px`,
    borderStyle: styles.borderWidth > 0 ? 'solid' : 'none',
    borderColor: styles.borderColor,
    boxShadow: styles.boxShadow,
    position: 'relative',
  };

  return (
    <div
      onClick={onClick}
      style={sectionStyle}
      className={`transition-all cursor-pointer ${
        isSelected ? "ring-2 ring-[#D4AF37] ring-inset" : "hover:ring-1 hover:ring-[rgba(212,175,55,0.3)]"
      }`}
    >
      {/* Background Overlay */}
      {styles.backgroundImage && styles.backgroundOverlay > 0 && (
        <div 
          className="absolute inset-0 bg-black pointer-events-none"
          style={{ opacity: styles.backgroundOverlay / 100 }}
        />
      )}
      
      {/* Content */}
      <div 
        className="relative mx-auto"
        style={{ maxWidth: styles.maxWidth === "full" ? "100%" : `${styles.maxWidth}px` }}
      >
        <SectionPreviewContent section={section} />
      </div>
    </div>
  );
};

// Section Content Preview
const SectionPreviewContent = ({ section }) => {
  const { type, content } = section;
  
  switch (type) {
    case "hero":
      return (
        <div 
          className="min-h-[40vh] flex items-center justify-center relative"
          style={{ 
            minHeight: content.minHeight || "60vh",
            backgroundImage: content.background_image ? `url(${getImageUrl(content.background_image)})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {content.background_image && (
            <div className="absolute inset-0 bg-black" style={{ opacity: (content.overlay_opacity || 60) / 100 }} />
          )}
          <div className={`relative z-10 px-6 text-${content.alignment || 'center'} w-full max-w-4xl`}>
            <h1 className="text-4xl md:text-6xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
              {content.title || "Hero Title"}
            </h1>
            <p className="text-xl text-[#A1A1AA] mb-8">{content.subtitle || "Subtitle goes here"}</p>
            {content.cta_text && (
              <button className={`px-8 py-4 font-semibold transition-colors ${
                content.cta_style === 'outline' 
                  ? 'border-2 border-[#D4AF37] text-[#D4AF37]'
                  : content.cta_style === 'ghost'
                  ? 'text-[#D4AF37] underline'
                  : 'bg-[#D4AF37] text-[#050505]'
              }`}>
                {content.cta_text}
              </button>
            )}
          </div>
        </div>
      );
      
    case "text":
      return (
        <div className={`text-${content.alignment || 'left'}`}>
          {content.heading && (
            <h2 className={`text-${content.headingSize || '3xl'} font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4`}>
              {content.heading}
            </h2>
          )}
          <div 
            className="prose prose-lg prose-invert max-w-none text-[#A1A1AA]"
            dangerouslySetInnerHTML={{ __html: content.text || "<p>Your content here...</p>" }}
          />
        </div>
      );
      
    case "spacer":
      return (
        <div style={{ height: `${content.height || 60}px` }} className="relative">
          {content.showLine && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/2 h-px bg-[rgba(212,175,55,0.3)]" />
          )}
        </div>
      );
      
    case "faq":
      return (
        <div className="max-w-3xl mx-auto">
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-8 text-center">
              {content.title}
            </h2>
          )}
          <div className="space-y-4">
            {(content.items || [{ question: "Sample Question?", answer: "Sample answer goes here." }]).map((item, i) => (
              <div key={i} className="border border-[rgba(212,175,55,0.2)] p-4">
                <h3 className="font-semibold text-[#F5F5F0] flex items-center justify-between">
                  {item.question || `Question ${i + 1}`}
                  <ChevronDownIcon className="w-5 h-5 text-[#D4AF37]" />
                </h3>
                <p className="text-[#A1A1AA] mt-2">{item.answer || "Answer"}</p>
              </div>
            ))}
          </div>
        </div>
      );
      
    case "team":
      const members = content.members || [
        { name: "Team Member", role: "Role" },
        { name: "Team Member", role: "Role" },
        { name: "Team Member", role: "Role" }
      ];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          <div className={`grid md:grid-cols-${content.columns || 3} gap-8`}>
            {members.map((member, i) => (
              <div key={i} className="text-center">
                {member.image ? (
                  <img src={getImageUrl(member.image)} alt={member.name} className="w-32 h-32 mx-auto rounded-full object-cover mb-4" />
                ) : (
                  <div className="w-32 h-32 mx-auto rounded-full bg-[#1A1A1A] mb-4 flex items-center justify-center">
                    <Users className="w-12 h-12 text-[#a1a1aa]" />
                  </div>
                )}
                <h3 className="font-semibold text-[#F5F5F0]">{member.name}</h3>
                <p className="text-[#A1A1AA] text-sm">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      );
      
    case "features":
      const features = content.features || [
        { icon: "star", title: "Feature 1", description: "Description" },
        { icon: "star", title: "Feature 2", description: "Description" },
        { icon: "star", title: "Feature 3", description: "Description" }
      ];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          <div className={`grid md:grid-cols-${content.columns || 3} gap-8`}>
            {features.map((feature, i) => (
              <div key={i} className="text-center p-6 border border-[rgba(212,175,55,0.1)]">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-[#F5F5F0] mb-2">{feature.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      );
      
    case "stats":
      const stats = content.stats || [
        { value: "100", suffix: "+", label: "Projects" },
        { value: "50", suffix: "K", label: "Customers" },
        { value: "99", suffix: "%", label: "Satisfaction" }
      ];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-[#D4AF37]">
                  {stat.value}{stat.suffix}
                </div>
                <p className="text-[#A1A1AA] mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      );
      
    case "countdown":
      return (
        <div className="text-center">
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-8">
              {content.title}
            </h2>
          )}
          <div className="flex justify-center gap-4">
            {["Days", "Hours", "Minutes", "Seconds"].map((unit) => (
              <div key={unit} className="p-4 bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] min-w-[80px]">
                <div className="text-3xl font-bold text-[#D4AF37]">00</div>
                <div className="text-xs text-[#A1A1AA] mt-1">{unit}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case "image":
      return (
        <div className={`text-${content.alignment || 'center'}`}>
          {content.image ? (
            <div style={{ maxWidth: content.size === 'small' ? '50%' : content.size === 'medium' ? '75%' : '100%', margin: content.alignment === 'center' ? '0 auto' : undefined, marginLeft: content.alignment === 'right' ? 'auto' : undefined }}>
              <img 
                src={getImageUrl(content.image)} 
                alt={content.alt || ""} 
                className="w-full h-auto rounded"
              />
              {content.caption && (
                <p className="text-[#A1A1AA] text-sm mt-2 italic">{content.caption}</p>
              )}
            </div>
          ) : (
            <div className="bg-[#1A1A1A] h-48 flex items-center justify-center">
              <Image className="w-12 h-12 text-[#a1a1aa]" />
            </div>
          )}
        </div>
      );

    case "divider":
      return (
        <div className="flex justify-center py-4">
          {content.style === 'ornament' ? (
            <div className="flex items-center gap-4">
              <div className="w-16 h-px bg-[#D4AF37]" />
              <div className="w-2 h-2 bg-[#D4AF37] transform rotate-45" />
              <div className="w-16 h-px bg-[#D4AF37]" />
            </div>
          ) : content.style === 'gradient' ? (
            <div 
              className="h-px"
              style={{ 
                width: content.width || '50%',
                background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)'
              }}
            />
          ) : (
            <div 
              style={{ 
                width: content.width || '50%',
                height: `${content.thickness || 1}px`,
                backgroundColor: content.color || '#D4AF37',
                borderStyle: content.style || 'solid'
              }}
            />
          )}
        </div>
      );

    case "two_column":
      const splitRatios = {
        '50-50': ['50%', '50%'],
        '33-67': ['33%', '67%'],
        '67-33': ['67%', '33%'],
        '40-60': ['40%', '60%'],
        '60-40': ['60%', '40%']
      };
      const [leftWidth, rightWidth] = splitRatios[content.split] || ['50%', '50%'];
      return (
        <div className={`grid md:grid-cols-2 gap-${content.gap || '8'}`} style={{ gridTemplateColumns: `${leftWidth} ${rightWidth}` }}>
          <div 
            className="prose prose-invert text-[#A1A1AA]"
            dangerouslySetInnerHTML={{ __html: content.leftContent || '<p>Left column content</p>' }}
          />
          <div 
            className="prose prose-invert text-[#A1A1AA]"
            dangerouslySetInnerHTML={{ __html: content.rightContent || '<p>Right column content</p>' }}
          />
        </div>
      );

    case "three_column":
      const cols = content.columns || ['', '', ''];
      return (
        <div className={`grid md:grid-cols-3 gap-${content.gap || '6'}`}>
          {cols.map((col, i) => (
            <div 
              key={i}
              className="prose prose-invert text-[#A1A1AA]"
              dangerouslySetInnerHTML={{ __html: col || `<p>Column ${i + 1}</p>` }}
            />
          ))}
        </div>
      );

    case "cta":
      return (
        <div className={`text-${content.alignment || 'center'}`}>
          {content.heading && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
              {content.heading}
            </h2>
          )}
          {content.subtext && (
            <p className="text-[#A1A1AA] mb-6">{content.subtext}</p>
          )}
          {content.buttonText && (
            <button className={`px-8 py-3 font-semibold transition-colors ${
              content.buttonStyle === 'outline' 
                ? 'border-2 border-[#D4AF37] text-[#D4AF37]'
                : content.buttonStyle === 'ghost'
                ? 'text-[#D4AF37] underline'
                : 'bg-[#D4AF37] text-[#050505]'
            }`}>
              {content.buttonText}
            </button>
          )}
        </div>
      );

    case "testimonial":
      return (
        <div className="max-w-2xl mx-auto text-center">
          <Quote className="w-10 h-10 text-[#D4AF37] mx-auto mb-6 opacity-50" />
          <p className="text-xl italic text-[#F5F5F0] mb-6">
            "{content.quote || 'Your testimonial quote here...'}"
          </p>
          <div className="flex items-center justify-center gap-4">
            {content.authorImage ? (
              <img src={getImageUrl(content.authorImage)} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                <Users className="w-6 h-6 text-[#a1a1aa]" />
              </div>
            )}
            <div className="text-left">
              <p className="font-semibold text-[#F5F5F0]">{content.authorName || 'Customer Name'}</p>
              <p className="text-[#A1A1AA] text-sm">{content.authorTitle || 'Title'}</p>
            </div>
          </div>
          {content.rating > 0 && (
            <div className="flex justify-center gap-1 mt-4">
              {[...Array(content.rating || 5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-[#D4AF37] fill-current" />
              ))}
            </div>
          )}
        </div>
      );

    case "testimonials_grid":
      const testimonials = content.testimonials || [
        { quote: "Great service!", authorName: "Customer 1", authorTitle: "Company" },
        { quote: "Highly recommend!", authorName: "Customer 2", authorTitle: "Company" }
      ];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          <div className={`grid md:grid-cols-${content.columns || 3} gap-6`}>
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 border border-[rgba(212,175,55,0.2)] bg-[#0A0A0A]">
                <Quote className="w-6 h-6 text-[#D4AF37] mb-4 opacity-50" />
                <p className="text-[#F5F5F0] italic mb-4">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-[#D4AF37]">{t.authorName}</p>
                  <p className="text-[#A1A1AA] text-sm">{t.authorTitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "video":
      // Extract video ID from YouTube or Vimeo URLs
      const getVideoEmbed = (url) => {
        if (!url) return null;
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
        if (youtubeMatch) {
          return `https://www.youtube.com/embed/${youtubeMatch[1]}${content.autoplay ? '?autoplay=1&mute=1' : ''}`;
        }
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
          return `https://player.vimeo.com/video/${vimeoMatch[1]}${content.autoplay ? '?autoplay=1&muted=1' : ''}`;
        }
        return url;
      };
      
      const aspectRatios = {
        '16:9': 'pb-[56.25%]',
        '4:3': 'pb-[75%]',
        '1:1': 'pb-[100%]',
        '9:16': 'pb-[177.78%]'
      };
      
      return (
        <div style={{ maxWidth: content.maxWidth === 'full' ? '100%' : `${content.maxWidth}px`, margin: '0 auto' }}>
          {content.title && (
            <h3 className="text-xl font-semibold text-[#F5F5F0] mb-4 text-center">{content.title}</h3>
          )}
          {content.url ? (
            <div className={`relative ${aspectRatios[content.aspectRatio] || 'pb-[56.25%]'}`}>
              <iframe
                src={getVideoEmbed(content.url)}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className={`relative ${aspectRatios[content.aspectRatio] || 'pb-[56.25%]'} bg-[#1A1A1A]`}>
              <div className="absolute inset-0 flex items-center justify-center">
                <Video className="w-12 h-12 text-[#a1a1aa]" />
              </div>
            </div>
          )}
        </div>
      );

    case "gallery":
      const galleryImages = content.images || [];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          {galleryImages.length > 0 ? (
            <div className={`grid grid-cols-2 md:grid-cols-${content.columns || 3} gap-4`}>
              {galleryImages.map((img, i) => (
                <div key={i} className="relative group overflow-hidden">
                  <img 
                    src={getImageUrl(img.url)} 
                    alt={img.caption || ''} 
                    className="w-full h-48 object-cover transition-transform group-hover:scale-105"
                  />
                  {img.caption && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <p className="text-white text-sm">{img.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 bg-[#1A1A1A] flex items-center justify-center">
                  <Images className="w-8 h-8 text-[#a1a1aa]" />
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case "contact_form":
      const formFields = content.fields || ["name", "email", "message"];
      return (
        <div className="max-w-xl mx-auto">
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4 text-center">
              {content.title}
            </h2>
          )}
          {content.description && (
            <p className="text-[#A1A1AA] mb-8 text-center">{content.description}</p>
          )}
          <div className="space-y-4">
            {formFields.includes("name") && (
              <input 
                type="text" 
                placeholder="Your Name" 
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3"
                disabled
              />
            )}
            {formFields.includes("email") && (
              <input 
                type="email" 
                placeholder="Your Email" 
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3"
                disabled
              />
            )}
            {formFields.includes("phone") && (
              <input 
                type="tel" 
                placeholder="Phone Number" 
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3"
                disabled
              />
            )}
            {formFields.includes("subject") && (
              <input 
                type="text" 
                placeholder="Subject" 
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3"
                disabled
              />
            )}
            {formFields.includes("message") && (
              <textarea 
                placeholder="Your Message" 
                rows={4}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 resize-none"
                disabled
              />
            )}
            <button className="w-full py-3 bg-[#D4AF37] text-[#050505] font-semibold" disabled>
              {content.submitText || "Send Message"}
            </button>
          </div>
        </div>
      );

    case "pricing":
      const pricingPlans = content.plans || [
        { name: "Basic", price: "$29", period: "month", features: ["Feature 1", "Feature 2"], buttonText: "Get Started" },
        { name: "Pro", price: "$59", period: "month", features: ["Feature 1", "Feature 2", "Feature 3"], buttonText: "Get Started", highlighted: true },
        { name: "Enterprise", price: "$99", period: "month", features: ["All Features"], buttonText: "Contact Us" }
      ];
      return (
        <div>
          {content.title && (
            <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10 text-center">
              {content.title}
            </h2>
          )}
          <div className={`grid md:grid-cols-${pricingPlans.length > 3 ? 4 : pricingPlans.length} gap-6`}>
            {pricingPlans.map((plan, i) => (
              <div 
                key={i} 
                className={`p-6 border ${
                  plan.highlighted 
                    ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                    : 'border-[rgba(212,175,55,0.2)]'
                } relative`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-4 py-1 bg-[#D4AF37] text-[#050505] text-xs font-semibold">
                    POPULAR
                  </div>
                )}
                <h3 className="text-xl font-semibold text-[#F5F5F0] mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#D4AF37]">{plan.price}</span>
                  {plan.period && <span className="text-[#A1A1AA]">/{plan.period}</span>}
                </div>
                <ul className="space-y-2 mb-6">
                  {(plan.features || []).map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-[#A1A1AA]">
                      <span className="text-[#D4AF37]">✓</span> {feature}
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-2 font-semibold ${
                  plan.highlighted
                    ? 'bg-[#D4AF37] text-[#050505]'
                    : 'border border-[#D4AF37] text-[#D4AF37]'
                }`}>
                  {plan.buttonText || "Get Started"}
                </button>
              </div>
            ))}
          </div>
        </div>
      );
      
    default:
      return (
        <div className="text-center py-8 text-[#a1a1aa]">
          <Layout className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{type} section</p>
        </div>
      );
  }
};

export default VisualPageBuilder;
